import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';

// ==================== PROVERBS SERVICE ====================
export const proverbsService = {
  // Get next proverb ID (optimized with ordering, checks both id and proverbId fields)
  async getNextProverbId() {
    try {
      const proverbsRef = collection(db, 'proverbs');
      
      // Try to get the highest ID using ordering (more efficient)
      // First try ordering by 'id' field
      try {
        const orderedQuery = query(
          proverbsRef, 
          orderBy('id', 'desc'), 
          limit(1)
        );
        
        const snapshot = await getDocs(orderedQuery);
        if (snapshot.docs.length > 0) {
          const highestProverb = snapshot.docs[0].data();
          const maxId = highestProverb.id || highestProverb.proverbId || 0;
          console.log(`🔢 Found highest proverb ID (via 'id' field): ${maxId}, next will be: ${maxId + 1}`);
          return maxId + 1;
        }
      } catch (queryError) {
        console.warn('⚠️ Ordered query by "id" failed, trying "proverbId":', queryError);
        
        // Try ordering by 'proverbId' field as fallback
        try {
          const orderedQuery = query(
            proverbsRef, 
            orderBy('proverbId', 'desc'), 
            limit(1)
          );
          
          const snapshot = await getDocs(orderedQuery);
          if (snapshot.docs.length > 0) {
            const highestProverb = snapshot.docs[0].data();
            const maxId = highestProverb.proverbId || highestProverb.id || 0;
            console.log(`🔢 Found highest proverb ID (via 'proverbId' field): ${maxId}, next will be: ${maxId + 1}`);
            return maxId + 1;
          }
        } catch (secondQueryError) {
          console.warn('⚠️ Both ordered queries failed, falling back to full scan:', secondQueryError);
        }
      }
      
      // Fallback: scan all documents (for backward compatibility)
      const snapshot = await getDocs(proverbsRef);
      let maxId = 0;
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Check both id and proverbId fields to find the highest
        const currentId = Math.max(
          (data.id && typeof data.id === 'number') ? data.id : 0,
          (data.proverbId && typeof data.proverbId === 'number') ? data.proverbId : 0
        );
        maxId = Math.max(maxId, currentId);
      });
      
      console.log(`🔢 Scanned ${snapshot.docs.length} documents, highest ID: ${maxId}, next will be: ${maxId + 1}`);
      return maxId + 1;
    } catch (error) {
      console.error('Error getting next proverb ID:', error);
      throw new Error('Failed to get next proverb ID');
    }
  },

  // Get all proverbs (sorted by numeric ID descending - newest first)
  async getAllProverbs() {
    try {
      const proverbsRef = collection(db, 'proverbs');
      const snapshot = await getDocs(proverbsRef);
      const proverbs = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          // Document ID is now the numeric ID (converted to string by Firebase)
          firebaseDocId: doc.id,
          // Use the numeric ID from the document ID
          id: parseInt(doc.id),
          ...data
        };
      });
      
      // Sort by numeric ID in descending order (newest/highest first)
      return proverbs.sort((a, b) => {
        const aId = a.id || 0;
        const bId = b.id || 0;
        return bId - aId;
      });
    } catch (error) {
      console.error('Error fetching proverbs:', error);
      throw new Error('Failed to fetch proverbs');
    }
  },

  // Get proverb by numeric ID
  async getProverbById(id) {
    try {
      // Convert to string since Firebase document IDs are strings
      const docId = id.toString();
      const proverbRef = doc(db, 'proverbs', docId);
      const snapshot = await getDoc(proverbRef);
      
      if (snapshot.exists()) {
        const data = snapshot.data();
        return {
          firebaseDocId: snapshot.id,
          // Parse the document ID back to number for consistency
          id: parseInt(snapshot.id),
          ...data
        };
      } else {
        throw new Error(`Proverb with ID ${id} not found`);
      }
    } catch (error) {
      console.error('Error fetching proverb:', error);
      throw error;
    }
  },

  // Add new proverb with auto-incremented ID in the correct format
  async addProverb(proverbData) {
    try {
      // Get the next proverb ID
      const nextProverbId = await this.getNextProverbId();
      
      // Prepare proverb data with the exact structure you specified
      const proverbWithId = {
        // Chinese proverb data
        chinese_proverb: proverbData.chinese_proverb || '',
        pinyin: proverbData.pinyin || '',
        
        // English translations
        english_translation: proverbData.english_translation || '',
        english_explanation: proverbData.english_explanation || '',
        english_equivalent: proverbData.english_equivalent || '',
        
        // French translations
        french_translation: proverbData.french_translation || '',
        french_explanation: proverbData.french_explanation || '',
        french_equivalent: proverbData.french_equivalent || '',
        
        // Arabic translations
        arabic_translation: proverbData.arabic_translation || '',
        arabic_Explanation: proverbData.arabic_Explanation || '', // Note: capital 'E' as in your example
        
        // ID fields - both should have the same auto-increment value
        id: nextProverbId,        // Main ID field
        proverbId: nextProverbId, // Secondary ID field for compatibility
        
        // Status and metadata
        status: proverbData.status || 'active',
        created_by: proverbData.created_by || null,
        
        // Timestamps
        created_at: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updated_at: serverTimestamp() // Additional timestamp field as in your example
      };
      
      // Save to Firebase using the numeric ID as document ID
      const proverbsRef = collection(db, 'proverbs');
      const docRef = doc(proverbsRef, nextProverbId.toString()); // Use numeric ID as document ID
      await setDoc(docRef, proverbWithId);
      
      console.log(`✅ Added new proverb with numeric document ID: ${nextProverbId}`);
      console.log(`📝 Proverb content: "${proverbData.chinese_proverb || 'N/A'}"`);
      console.log(`🔢 Document ID is now: ${nextProverbId} (not auto-generated)`);
      
      return { 
        firebaseId: nextProverbId.toString(), // Document ID is the numeric ID
        proverbId: nextProverbId,
        id: nextProverbId,
        success: true
      };
    } catch (error) {
      console.error('Error adding proverb:', error);
      throw new Error(`Failed to add proverb: ${error.message}`);
    }
  },

  // Update proverb with proper field structure
  async updateProverb(id, proverbData) {
    try {
      // Convert to string since Firebase document IDs are strings
      const docId = id.toString();
      const proverbRef = doc(db, 'proverbs', docId);
      
      // Prepare update data with the correct structure
      const updateData = {
        // Chinese proverb data
        chinese_proverb: proverbData.chinese_proverb,
        pinyin: proverbData.pinyin,
        
        // English translations
        english_translation: proverbData.english_translation,
        english_explanation: proverbData.english_explanation || '',
        english_equivalent: proverbData.english_equivalent || '',
        
        // French translations
        french_translation: proverbData.french_translation || '',
        french_explanation: proverbData.french_explanation || '',
        french_equivalent: proverbData.french_equivalent || '',
        
        // Arabic translations
        arabic_translation: proverbData.arabic_translation || '',
        arabic_Explanation: proverbData.arabic_Explanation || '', // Note: capital 'E'
        
        // Update timestamps
        updatedAt: serverTimestamp(),
        updated_at: serverTimestamp()
      };
      
      await updateDoc(proverbRef, updateData);
      console.log(`✅ Updated proverb with numeric document ID: ${docId}`);
      return true;
    } catch (error) {
      console.error('Error updating proverb:', error);
      throw new Error('Failed to update proverb');
    }
  },

  // Delete proverb by numeric document ID
  async deleteProverb(id) {
    try {
      // Convert to string since Firebase document IDs are strings
      const docId = id.toString();
      const proverbRef = doc(db, 'proverbs', docId);
      await deleteDoc(proverbRef);
      console.log(`✅ Deleted proverb with numeric document ID: ${docId}`);
      return true;
    } catch (error) {
      console.error('Error deleting proverb:', error);
      throw new Error('Failed to delete proverb');
    }
  },

  // Utility: Fix proverbs with missing or invalid IDs (sets both id and proverbId)
  async fixProverbIds() {
    try {
      console.log('🔧 Checking and fixing proverb IDs...');
      
      const proverbsRef = collection(db, 'proverbs');
      const snapshot = await getDocs(proverbsRef);
      
      const proverbsToFix = [];
      const validProverbs = [];
      
      // Categorize proverbs
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const hasValidId = (data.id && typeof data.id === 'number' && data.id > 0) || 
                          (data.proverbId && typeof data.proverbId === 'number' && data.proverbId > 0);
        
        if (!hasValidId) {
          proverbsToFix.push({ id: doc.id, data });
        } else {
          // Use the highest valid ID between id and proverbId
          const validId = Math.max(
            (data.id && typeof data.id === 'number') ? data.id : 0,
            (data.proverbId && typeof data.proverbId === 'number') ? data.proverbId : 0
          );
          validProverbs.push({ id: doc.id, proverbId: validId });
        }
      });
      
      console.log(`📊 Found ${validProverbs.length} valid proverbs, ${proverbsToFix.length} need fixing`);
      
      if (proverbsToFix.length === 0) {
        console.log('✅ All proverbs already have valid IDs');
        return { fixed: 0, total: snapshot.docs.length };
      }
      
      // Find the highest existing ID
      let maxId = 0;
      validProverbs.forEach(proverb => {
        maxId = Math.max(maxId, proverb.proverbId);
      });
      
      console.log(`🔢 Starting ID assignment from: ${maxId + 1}`);
      
      // Fix proverbs without valid IDs
      let nextId = maxId + 1;
      for (const proverb of proverbsToFix) {
        try {
          const proverbRef = doc(db, 'proverbs', proverb.id);
          await updateDoc(proverbRef, {
            id: nextId,           // Set both fields
            proverbId: nextId,    // to the same value
            updatedAt: serverTimestamp()
          });
          console.log(`✅ Fixed proverb ${proverb.id} -> ID: ${nextId} (set both id and proverbId)`);
          nextId++;
        } catch (error) {
          console.error(`❌ Failed to fix proverb ${proverb.id}:`, error);
        }
      }
      
      console.log(`🎉 Fixed ${proverbsToFix.length} proverb IDs successfully`);
      return { fixed: proverbsToFix.length, total: snapshot.docs.length };
    } catch (error) {
      console.error('Error fixing proverb IDs:', error);
      throw new Error('Failed to fix proverb IDs');
    }
  },

  // Get proverbs with pagination
  async getProverbsPaginated(pageSize = 10, lastDoc = null) {
    try {
      let q = query(
        collection(db, 'proverbs'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const proverbs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return {
        proverbs,
        lastDoc: snapshot.docs[snapshot.docs.length - 1],
        hasMore: snapshot.docs.length === pageSize
      };
    } catch (error) {
      console.error('Error fetching paginated proverbs:', error);
      throw new Error('Failed to fetch proverbs');
    }
  }
};

// ==================== ACTIVITIES SERVICE ====================
export const activitiesService = {
  // Log user activity
  async logActivity(activityData) {
    try {
      const activitiesRef = collection(db, 'activities');
      const docRef = await addDoc(activitiesRef, {
        ...activityData,
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error logging activity:', error);
      throw new Error('Failed to log activity');
    }
  },

  // Get all activities
  async getAllActivities() {
    try {
      const activitiesRef = collection(db, 'activities');
      // Remove orderBy to avoid index requirement, we'll sort in JavaScript
      const snapshot = await getDocs(activitiesRef);
      const activities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort in JavaScript instead of Firestore
      return activities.sort((a, b) => {
        const timestampA = a.timestamp?.seconds ? a.timestamp.seconds * 1000 : new Date(a.timestamp || a.createdAt || 0).getTime();
        const timestampB = b.timestamp?.seconds ? b.timestamp.seconds * 1000 : new Date(b.timestamp || b.createdAt || 0).getTime();
        return timestampB - timestampA; // Descending order (newest first)
      });
    } catch (error) {
      console.error('Error fetching activities:', error);
      throw new Error('Failed to fetch activities');
    }
  },

  // Get activities by user
  async getActivitiesByUser(userId) {
    try {
      const activitiesRef = collection(db, 'activities');
      const q = query(
        activitiesRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching user activities:', error);
      throw new Error('Failed to fetch user activities');
    }
  },

  // Get activities by type
  async getActivitiesByType(activityType) {
    try {
      const activitiesRef = collection(db, 'activities');
      const q = query(
        activitiesRef,
        where('action', '==', activityType),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching activities by type:', error);
      throw new Error('Failed to fetch activities');
    }
  },

  // Get recent activities with limit
  async getRecentActivities(limitCount = 50) {
    try {
      const activitiesRef = collection(db, 'activities');
      const q = query(
        activitiesRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      throw new Error('Failed to fetch recent activities');
    }
  }
};

// ==================== VOICE SERVICE ====================
export const voiceService = {
  // Upload voice file and save metadata
  async uploadVoice(proverbId, audioFile, metadata = {}) {
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const filename = `voices/proverb_${proverbId}_${timestamp}.mp3`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, filename);
      const uploadResult = await uploadBytes(storageRef, audioFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Save metadata to Firestore
      const voiceRef = collection(db, 'voice');
      const voiceData = {
        proverbId,
        url: downloadURL,
        filename,
        originalName: audioFile.name,
        size: audioFile.size,
        type: audioFile.type,
        uploadedAt: serverTimestamp(),
        ...metadata
      };
      
      const docRef = await addDoc(voiceRef, voiceData);
      
      return {
        id: docRef.id,
        url: downloadURL,
        ...voiceData
      };
    } catch (error) {
      console.error('Error uploading voice:', error);
      throw new Error('Failed to upload voice file');
    }
  },

  // Get voice by proverb ID
  async getVoiceByProverbId(proverbId) {
    try {
      const voiceRef = collection(db, 'voice');
      const q = query(voiceRef, where('proverbId', '==', proverbId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching voice by proverb ID:', error);
      throw new Error('Failed to fetch voice files');
    }
  },

  // Get all voices
  async getAllVoices() {
    try {
      const voiceRef = collection(db, 'voice');
      const q = query(voiceRef, orderBy('uploadedAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching voices:', error);
      throw new Error('Failed to fetch voice files');
    }
  },

  // Delete voice file
  async deleteVoice(voiceId) {
    try {
      // Get voice document to get storage path
      const voiceDocRef = doc(db, 'voice', voiceId);
      const voiceDoc = await getDoc(voiceDocRef);
      
      if (!voiceDoc.exists()) {
        throw new Error('Voice file not found');
      }
      
      const voiceData = voiceDoc.data();
      
      // Delete from Firebase Storage
      if (voiceData.filename) {
        const storageRef = ref(storage, voiceData.filename);
        await deleteObject(storageRef);
      }
      
      // Delete from Firestore
      await deleteDoc(voiceDocRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting voice:', error);
      throw new Error('Failed to delete voice file');
    }
  },

  // Update voice metadata
  async updateVoiceMetadata(voiceId, metadata) {
    try {
      const voiceRef = doc(db, 'voice', voiceId);
      await updateDoc(voiceRef, {
        ...metadata,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating voice metadata:', error);
      throw new Error('Failed to update voice metadata');
    }
  }
};

// ==================== UTILITY FUNCTIONS ====================
export const firebaseUtils = {
  // Activity logging helper
  async logUserActivity(userId, userName, action, details = {}) {
    return await activitiesService.logActivity({
      userId,
      userName,
      action,
      details,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  },

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const [proverbs, activities, voices] = await Promise.all([
        proverbsService.getAllProverbs(),
        activitiesService.getRecentActivities(100),
        voiceService.getAllVoices()
      ]);

      return {
        totalProverbs: proverbs.length,
        totalActivities: activities.length,
        totalVoices: voices.length,
        recentActivities: activities.slice(0, 10),
        proverbsWithVoice: voices.length // Assuming each voice corresponds to one proverb
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      throw new Error('Failed to get dashboard statistics');
    }
  }
};

const firebaseService = {
  proverbs: proverbsService,
  activities: activitiesService,
  voice: voiceService,
  utils: firebaseUtils
};

export default firebaseService;
